'use client'
import { useEffect, useState } from 'react'
import classNames from 'classnames'
import { Slider, Button } from '@achieve/ascend'
import { MediaImageStandard, Typography } from 'components/Contentful'
import { AchieveLink } from 'components/AchieveLink/AchieveLink'
import Section from 'components/Section'
import { mainProductConfig } from 'constants/mainProductConfig'
import SITE_THEME from 'constants/websites'
import TrustpilotRatingBadge from 'components/TrustpilotRatingBadge'
import { useMemoizedContentGetter } from 'utils/contentful'
import Cookies from 'js-cookie'

const dollarUSLocale = Intl.NumberFormat('en-US')

const makeButtonStyles = (type = 'primary', website = SITE_THEME.ACHIEVE) => {
  const BUTTON_STYLE = {
    [SITE_THEME.FDR]: {
      primary: classNames(
        'bg-[#cb000e]',
        'hover:bg-[#7A0D0D]',
        'hover:text-[white]',
        'leading-[1.2]',
        'w-full',
        'px-4',
        'lg:px-6'
      ),
      secondary: classNames('text-blue-600', 'w-full', 'lg:w-auto', 'hover:bg-[#EFF5FF]'),
    },
  }

  if (BUTTON_STYLE[website] && BUTTON_STYLE[website][type]) {
    return BUTTON_STYLE[website][type]
  }

  return undefined
}

const stringToNumber = (str) => {
  return Number(str.replace(/,/g, ''))
}

function HomeHeroV3({ content }) {
  const {
    subheading,
    title,
    titleMobile,
    sliderTitle,
    sliderConfig = {},
    button,
    trustpilotInfo,
    mainImage,
    mainImageMobile,
    secondaryImage,
    testimonialQuote,
    testimonialDisclaimer,
    ReviewsSectionArray,
  } = useMemoizedContentGetter(content, [
    'subheading',
    'title',
    'titleMobile',
    'sliderTitle',
    'sliderConfig',
    'button',
    'trustpilotInfo',
    'mainImage',
    'mainImageMobile',
    'secondaryImage',
    'testimonialQuote',
    'testimonialDisclaimer',
    'ReviewsSectionArray',
  ])

  return (
    <HomeHeroV3Module
      subheading={subheading}
      title={title}
      titleMobile={titleMobile}
      sliderTitle={sliderTitle}
      sliderConfig={sliderConfig}
      button={button}
      trustpilotInfo={trustpilotInfo}
      mainImage={mainImage}
      mainImageMobile={mainImageMobile}
      secondaryImage={secondaryImage}
      testimonialQuote={testimonialQuote}
      testimonialDisclaimer={testimonialDisclaimer}
      ReviewsSectionArray={ReviewsSectionArray}
    />
  )
}

const HomeHeroV3Module = ({
  subheading,
  title,
  titleMobile,
  sliderTitle,
  sliderConfig,
  button,
  trustpilotInfo,
  mainImage,
  mainImageMobile,
  secondaryImage,
  testimonialQuote,
  testimonialDisclaimer,
  ReviewsSectionArray,
}) => {
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'
  const leadId = Cookies.get('leadId') || ''

  const slider = sliderConfig.jsonContent
  const { outOf, link, ...props } = trustpilotInfo.jsonContent || {}
  const { totalRating: rating, totalReviews } = ReviewsSectionArray.jsonContent || {}

  const ratingToNumber = stringToNumber(rating)
  const totalReviewsToNumber = stringToNumber(totalReviews)

  const [sliderValue, setSliderValue] = useState(slider.defaultValue)
  const [deviceWidth, setDeviceWidth] = useState(0)

  function handleChange(e) {
    setSliderValue(e.target.value)
  }

  const makeTrackObject = (text = '', url = '') => ({
    click_id: `${mainProductConfig[siteEnv || 'achieve']?.['MainProduct']} | ${text}`,
    click_text: `${mainProductConfig[siteEnv || 'achieve']?.['MainProduct']} | ${text}`,
    nav_link_section: 'Hero Card',
    track_event: 'button_click',
    event_action: 'link_click',
    event_type: 'link_click',
    click_url: url,
  })

  const handleButtonClick = (link = '') => {
    if (typeof window !== 'undefined') window.location.href = link
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setDeviceWidth(window.innerWidth)
    }
  }, [])

  return (
    <Section data-testid="home-hero-section" className="bg-neutral-25" contain={false}>
      <div className="lg:flex w-full">
        <div className="block w-full lg:hidden">
          <MediaImageStandard
            content={mainImageMobile.mediaContent}
            objectFit="cover"
            width={deviceWidth}
            height={240}
            priority
            alt={mainImageMobile.mediaAltText}
          />
        </div>
        <div className="relative w-full px-5 z-10 translate-y-[-55px] lg:translate-y-0 lg:w-1/2 lg:p-10">
          <Typography
            className={classNames(
              'hidden',
              'text-[14px]',
              'font-bold',
              'leading-[120%]',
              'tracking-[.25em]',
              'text-center',
              'text-neutral-500',
              'lg:block'
            )}
            content={subheading.textContent}
          />
          <Typography
            className={classNames(
              'hidden',
              'text-[28px]',
              'font-medium',
              'leading-[150%]',
              'text-center',
              'py-[8px]',
              'lg:py-0',
              'lg:text-[48px]',
              'lg:font-normal',
              'lg:block'
            )}
            content={title.textContent}
          />
          <div className="w-full my-3 flex flex-col items-center rounded-[8px] bg-white py-[24px] px-[16px] text-center shadow-2 lg:my-4 lg:px-0">
            <Typography
              className={classNames(
                'block',
                'text-[14px]',
                'font-bold',
                'leading-[120%]',
                'tracking-[.25em]',
                'text-center',
                'text-neutral-500',
                'lg:hidden'
              )}
              content={subheading.textContent}
            />
            <Typography
              className={classNames(
                'block',
                'text-[28px]',
                'font-medium',
                'leading-[150%]',
                'text-center',
                'py-[8px]',
                'lg:py-0',
                'lg:text-[48px]',
                'lg:font-normal',
                'lg:hidden'
              )}
              content={titleMobile.textContent}
            />
            <div className="w-full max-w-[303px] bg-[#f8f9ff] px-7 py-3 rounded-[8px] lg:rounded-none lg:px-0 lg:py-0 lg:w-4/5 lg:bg-transparent lg:max-w-full">
              <Typography
                className={classNames(
                  'text-[16px]',
                  'font-medium',
                  'leading-[150%]',
                  'text-center',
                  'mb-[8px]',
                  'lg:text-[20px]',
                  'lg:font-normal',
                  'lg:mb-[16px]'
                )}
                content={sliderTitle.textContent}
              />
              <Typography
                className={classNames(
                  'text-[20px]',
                  'leading-[150%]',
                  'text-center',
                  'text-neutral-500',
                  'mb-[0.2rem]',
                  'lg:text-[28px]'
                )}
                content={`$${dollarUSLocale.format(sliderValue)}`}
                data-testid="home-hero-dollar-value"
              />
              <Slider
                aria-label="home-hero-slider"
                data-testid="home-hero-slider-input"
                value={sliderValue}
                step={slider.step}
                min={slider.min}
                max={slider.max}
                classes={{
                  mark: 'hidden',
                }}
                marks={[
                  {
                    value: slider.min,
                    label: (
                      <Typography
                        content={`$${dollarUSLocale.format(slider.min)}`}
                        data-testid="home-hero-slider-min"
                        className="text-[14px] leading-[150%] text-[#4a4a4a] translate-x-[50%]"
                      />
                    ),
                  },
                  {
                    value: slider.max,
                    label: (
                      <Typography
                        content={`$${dollarUSLocale.format(slider.max)}${
                          slider.maxPlus ? '+' : ''
                        }`}
                        data-testid="home-hero-slider-max"
                        className="text-[14px] leading-[150%] text-[#4a4a4a] translate-x-[-50%]"
                      />
                    ),
                  },
                ]}
                onChange={handleChange}
              />
            </div>
            {button && (
              <div className="w-full max-w-[303px] lg:max-w-[327px] mt-[20px] lg:mt-[10px]">
                <AchieveLink
                  noLink
                  track={makeTrackObject(
                    button?.linkText?.json.content[0]?.content[0]?.value,
                    button?.linkHref
                      .replace('{leadId}', leadId)
                      .replace('{estimated_debt}', sliderValue)
                  )}
                >
                  <Button
                    className={makeButtonStyles('primary', siteEnv)}
                    variant="contained"
                    onClick={() =>
                      handleButtonClick(
                        button?.linkHref
                          .replace('{leadId}', leadId)
                          .replace('{estimated_debt}', sliderValue)
                      )
                    }
                    data-testid="hero-brand-primary-button"
                  >
                    {button?.linkText?.json.content[0]?.content[0]?.value}
                  </Button>
                </AchieveLink>
              </div>
            )}
            {rating && totalReviews && outOf && link && (
              <div className="w-full lg:justify-self-start mt-4">
                <TrustpilotRatingBadge
                  {...props}
                  outOf={outOf}
                  link={link}
                  rating={ratingToNumber}
                  totalReviews={totalReviewsToNumber}
                />
              </div>
            )}
          </div>
        </div>
        <div className="hidden lg:w-1/2 lg:block">
          <div className="flex flex-col items-center text-center p-6">
            <div className="relative w-100 h-100">
              <MediaImageStandard
                content={mainImage.mediaContent}
                aspectRatio={1}
                objectFit="contain"
                priority
                alt={mainImage.mediaAltText}
              />
              <div className="absolute top-0 right-0 w-[81px] h-[81px] z-10">
                <MediaImageStandard
                  content={secondaryImage.mediaContent}
                  aspectRatio={1}
                  objectFit="contain"
                  priority
                  alt={secondaryImage.mediaAltText}
                />
              </div>
            </div>
            <div className="bg-[#002d87] mt-[-25px] py-2 px-3 w-[384px] rounded-[8px] relative z-5">
              <Typography
                className="text-white text-[14px] leading-[150%]"
                content={testimonialQuote.textContent}
              />
            </div>
            <div className="mt-4 w-[384px]">
              <Typography
                className="text-[#4a4a4a] text-[12px] leading-[16px]"
                content={testimonialDisclaimer.textContent}
              />
            </div>
          </div>
        </div>
      </div>
    </Section>
  )
}

export { HomeHeroV3, HomeHeroV3Module }
export default HomeHeroV3
